/**
 * CollectionFilterPills
 * ─────────────────────
 * Client-side pill filter for product grids. Reads a product→matched-keys
 * map emitted by the Liquid section, stamps cards with data-filter-match,
 * and toggles visibility based on the active filter set.
 *
 * Union semantics: card shows when no filters active OR when any of its
 * matched keys is in the active set.
 *
 * URL sync: ?filters=key1,key2 (read on load, written on change).
 *
 * Each `[data-cfp-root]` element gets its own instance so multiple filters
 * can coexist on one page.
 */
(function () {
  'use strict';

  class CollectionFilterPills {
    constructor(rootEl) {
      this.root = rootEl;
      this.active = new Set();

      // Parse the emitted product→keys map.
      const dataEl = rootEl.querySelector('[data-cfp-data]');
      try {
        this.productMap = dataEl ? JSON.parse(dataEl.textContent) : {};
      } catch (_e) {
        this.productMap = {};
      }

      // Resolve the card container (defaults to the collection grid).
      const selector = rootEl.dataset.cfpCardContainer || '#product-grid';
      this.cardContainer = document.querySelector(selector);

      // Grid structure: <ul><li>[inner card]</li></ul>. We filter at the <li>
      // level so hidden items don't leave empty grid slots, and so injected
      // cards without a product-id (e.g. "Bundle & Save" upsells) are also
      // hidden when filters are active.
      const grid = this.cardContainer;
      const directLis = grid ? Array.from(grid.children).filter((el) => el.tagName === 'LI') : [];
      this.cards = directLis.length
        ? directLis
        : (grid ? Array.from(grid.querySelectorAll('[data-product-id]')) : []);

      // Stamp matched keys from the product map onto each filterable item.
      // Items without a product-id (or without an entry in the map) end up
      // with no `data-filter-match` — they'll be hidden whenever any filter
      // is active (nothing to match against).
      this.cards.forEach((item) => {
        const inner = item.querySelector('[data-product-id]') || (item.dataset.productId ? item : null);
        const pid = inner && inner.dataset.productId;
        const keys = pid ? this.productMap[pid] : null;
        if (keys) item.dataset.filterMatch = keys;
      });

      this.pills = Array.from(rootEl.querySelectorAll('[data-cfp-key]'));
      this.activeRowEl = rootEl.querySelector('[data-cfp-active]');
      this.activeChipsEl = rootEl.querySelector('[data-cfp-active-chips]');
      this.clearEl = rootEl.querySelector('[data-cfp-clear]');

      this._bindEvents();
      this._hydrateFromUrl();
      this._render();
    }

    _bindEvents() {
      this.pills.forEach((pill) => {
        pill.addEventListener('click', () => this.toggle(pill.dataset.cfpKey));
      });
      if (this.clearEl) {
        this.clearEl.addEventListener('click', () => this.clear());
      }
    }

    _hydrateFromUrl() {
      const raw = new URLSearchParams(window.location.search).get('filters');
      if (!raw) return;
      raw.split(',').map((s) => s.trim()).filter(Boolean).forEach((k) => {
        // Only hydrate keys that actually exist as pills.
        if (this.pills.some((p) => p.dataset.cfpKey === k)) {
          this.active.add(k);
        }
      });
    }

    toggle(key) {
      // The "All" pill has an empty key — clicking it clears state.
      if (!key) {
        this.clear();
        return;
      }
      if (this.active.has(key)) this.active.delete(key);
      else this.active.add(key);
      this._render();
      this._syncUrl();
    }

    clear() {
      this.active.clear();
      this._render();
      this._syncUrl();
    }

    getActive() {
      return Array.from(this.active);
    }

    _render() {
      // Pill active states.
      this.pills.forEach((pill) => {
        const key = pill.dataset.cfpKey;
        const on = key === '' ? this.active.size === 0 : this.active.has(key);
        pill.classList.toggle('is-active', on);
      });

      // Card visibility (union / OR).
      this.cards.forEach((card) => {
        const matches = (card.dataset.filterMatch || '').split(/\s+/).filter(Boolean);
        const show = this.active.size === 0 || matches.some((m) => this.active.has(m));
        card.classList.toggle('cfp-hidden', !show);
      });

      // Active-filters row.
      if (!this.activeRowEl) return;
      if (this.active.size === 0) {
        this.activeRowEl.setAttribute('hidden', '');
        if (this.activeChipsEl) this.activeChipsEl.innerHTML = '';
        return;
      }
      this.activeRowEl.removeAttribute('hidden');
      if (this.activeChipsEl) {
        this.activeChipsEl.innerHTML = '';
        this.active.forEach((key) => {
          const sourcePill = this.pills.find((p) => p.dataset.cfpKey === key);
          const label = sourcePill
            ? sourcePill.querySelector('.cfp__pill-label').textContent
            : key;
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'cfp__chip';
          chip.dataset.cfpChipKey = key;
          chip.innerHTML =
            '<span>' + label + '</span>' +
            '<span class="cfp__chip-x" aria-label="Remove">×</span>';
          chip.addEventListener('click', () => this.toggle(key));
          this.activeChipsEl.appendChild(chip);
        });
      }
    }

    _syncUrl() {
      const url = new URL(window.location.href);
      if (this.active.size === 0) {
        url.searchParams.delete('filters');
      } else {
        url.searchParams.set('filters', Array.from(this.active).join(','));
      }
      const next = url.pathname + (url.search ? url.search : '') + url.hash;
      window.history.replaceState({}, '', next);
    }
  }

  // Expose for tests + external re-init.
  if (typeof window !== 'undefined') {
    window.NPCFP = CollectionFilterPills;
  }

  // Auto-init every root on DOM ready.
  function init() {
    document.querySelectorAll('[data-cfp-root]').forEach((el) => {
      if (!el.__cfpInstance) el.__cfpInstance = new CollectionFilterPills(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Support CommonJS for tests (vitest/jsdom).
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = CollectionFilterPills;
  }
})();
